import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Database, RefreshCw, Play, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * MigrationsPanel — one-click Phase 2 data migration for admins.
 * Idempotent: safe to run on production; only adds registration IDs, data-quality
 * flags, and initial club history entries where missing.
 */
const MigrationsPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/migrations/phase2/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load migration status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const totalPending =
    (status?.players_missing_registration_id || 0) +
    (status?.players_missing_data_quality || 0) +
    (status?.players_missing_club_history || 0);

  const run = async () => {
    if (!window.confirm(
      `Run Phase 2 migration?\n\nThis will:\n• Assign ABFA registration IDs to ${status?.players_missing_registration_id || 0} players\n• Backfill data-quality flags on ${status?.players_missing_data_quality || 0} players\n• Create initial club history for ${status?.players_missing_club_history || 0} players\n\nSafe & idempotent — no existing data will be overwritten.`
    )) return;

    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/migrations/phase2/run`, {}, { withCredentials: true });
      setLastRun(res.data);
      setStatus(res.data.status_after);
      const total = res.data.registration_ids_assigned + res.data.data_quality_backfilled + res.data.club_history_created;
      if (total > 0) {
        toast.success(`Migration complete — ${total} record${total === 1 ? "" : "s"} updated`);
      } else {
        toast.success("Migration ran — all players already up to date");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-white p-6 border border-gray-200" data-testid="migrations-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] flex items-center gap-2">
          <Database className="w-5 h-5 text-[#FFC72C]" />
          Data Migrations
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={loadStatus}
          disabled={loading || running}
          data-testid="btn-refresh-migration-status"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="mb-4 p-4 bg-gray-50 border-l-4 border-[#FFC72C]">
        <div className="flex items-center justify-between mb-2">
          <span className="font-heading uppercase text-sm">Phase 2 — Player Registration IDs</span>
          {totalPending === 0 ? (
            <Badge className="bg-[#009E60] text-white flex items-center gap-1 hover:bg-[#009E60]" data-testid="badge-migration-up-to-date">
              <CheckCircle2 className="w-3 h-3" /> Up to date
            </Badge>
          ) : (
            <Badge className="bg-orange-500 text-white flex items-center gap-1 hover:bg-orange-500" data-testid="badge-migration-pending">
              <AlertCircle className="w-3 h-3" /> {totalPending} pending
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-600 mb-3">
          Assigns lifetime ABFA registration IDs (<span className="font-mono">ABFA-{status?.registration_year || "2026"}-XXXXXX</span>) to every player and backfills career-history rows.
          Idempotent and additive — never overwrites existing data.
        </p>

        {status && !loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-xs">
            <div className="bg-white p-2 border border-gray-100" data-testid="stat-total-players">
              <div className="font-heading text-2xl font-bold text-[#09090b]">{status.total_players}</div>
              <div className="text-gray-500 uppercase">Total Players</div>
            </div>
            <div className={`bg-white p-2 border ${status.players_missing_registration_id > 0 ? "border-orange-300" : "border-gray-100"}`} data-testid="stat-missing-reg-id">
              <div className={`font-heading text-2xl font-bold ${status.players_missing_registration_id > 0 ? "text-orange-500" : "text-[#009E60]"}`}>
                {status.players_missing_registration_id}
              </div>
              <div className="text-gray-500 uppercase">Missing ID</div>
            </div>
            <div className={`bg-white p-2 border ${status.players_missing_data_quality > 0 ? "border-orange-300" : "border-gray-100"}`} data-testid="stat-missing-dq">
              <div className={`font-heading text-2xl font-bold ${status.players_missing_data_quality > 0 ? "text-orange-500" : "text-[#009E60]"}`}>
                {status.players_missing_data_quality}
              </div>
              <div className="text-gray-500 uppercase">Missing DQ Flag</div>
            </div>
            <div className={`bg-white p-2 border ${status.players_missing_club_history > 0 ? "border-orange-300" : "border-gray-100"}`} data-testid="stat-missing-history">
              <div className={`font-heading text-2xl font-bold ${status.players_missing_club_history > 0 ? "text-orange-500" : "text-[#009E60]"}`}>
                {status.players_missing_club_history}
              </div>
              <div className="text-gray-500 uppercase">Missing History</div>
            </div>
          </div>
        )}

        {status && (
          <p className="text-xs text-gray-500 mt-3">
            Next ID to be assigned: <span className="font-mono font-bold">{status.next_registration_id}</span>
          </p>
        )}
      </div>

      <Button
        onClick={run}
        disabled={loading || running}
        className="bg-[#FFC72C] text-black hover:bg-[#e6b325] w-full font-heading uppercase"
        data-testid="btn-run-phase2-migration"
      >
        {running ? (
          <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running…</>
        ) : (
          <><Play className="w-4 h-4 mr-2" /> Run Phase 2 Migration</>
        )}
      </Button>

      {lastRun && (
        <div className="mt-4 p-3 bg-[#009E60]/10 border border-[#009E60]/30 text-sm" data-testid="migration-last-result">
          <p className="font-heading font-bold text-[#009E60] mb-1 flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" /> Last run
          </p>
          <ul className="text-xs text-gray-700 space-y-0.5">
            <li>• {lastRun.registration_ids_assigned} registration IDs assigned</li>
            <li>• {lastRun.data_quality_backfilled} data-quality fields backfilled</li>
            <li>• {lastRun.club_history_created} club history entries created</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default MigrationsPanel;
